import { translate } from '@/i18n'

/**
 * @param {string} iso YYYY-MM-DD
 */
export function parseLocalDate(iso) {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * @param {Date} date
 */
export function formatIsoDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * @param {Date} date
 * @param {string} dateMin
 * @param {'en' | 'ar'} [locale]
 */
export function formatBookingDateLabel(date, dateMin, locale = 'en') {
  const iso = formatIsoDate(date)
  if (iso === dateMin) {
    return translate('publicBook.schedule.today', locale)
  }

  const tomorrow = parseLocalDate(dateMin)
  tomorrow.setDate(tomorrow.getDate() + 1)
  if (iso === formatIsoDate(tomorrow)) {
    return translate('publicBook.schedule.tomorrow', locale)
  }

  return date.toLocaleDateString(locale === 'ar' ? 'ar-AE' : 'en-GB', { weekday: 'short' })
}

/**
 * @param {string} dateMin YYYY-MM-DD
 * @param {string} dateMax YYYY-MM-DD
 * @param {number} [maxDays]
 * @param {'en' | 'ar'} [locale]
 */
export function buildSelectableDates(dateMin, dateMax, maxDays = 21, locale = 'en') {
  if (!dateMin || !dateMax) {
    return []
  }

  const dates = []
  const cursor = parseLocalDate(dateMin)
  const end = parseLocalDate(dateMax)
  const dateLocale = locale === 'ar' ? 'ar-AE' : 'en-GB'

  while (cursor <= end && dates.length < maxDays) {
    const value = formatIsoDate(cursor)
    dates.push({
      value,
      weekday: formatBookingDateLabel(cursor, dateMin, locale),
      day: cursor.getDate(),
      month: cursor.toLocaleDateString(dateLocale, { month: 'short' }),
    })
    cursor.setDate(cursor.getDate() + 1)
  }

  return dates
}

/**
 * @param {Array<{ starts_at: string, available?: boolean }>} slots
 * @param {'en' | 'ar'} [locale]
 */
export function groupSlotsByPeriod(slots, locale = 'en') {
  /** @type {Array<{ id: string, label: string, items: typeof slots }>} */
  const groups = [
    { id: 'morning', label: translate('publicBook.schedule.morning', locale), items: [] },
    { id: 'afternoon', label: translate('publicBook.schedule.afternoon', locale), items: [] },
    { id: 'evening', label: translate('publicBook.schedule.evening', locale), items: [] },
  ]

  for (const slot of slots) {
    const hour = new Date(slot.starts_at).getHours()
    if (hour < 12) {
      groups[0].items.push(slot)
    } else if (hour < 17) {
      groups[1].items.push(slot)
    } else {
      groups[2].items.push(slot)
    }
  }

  return groups.filter((group) => group.items.length > 0)
}

/**
 * @param {Array<{ starts_at: string, available?: boolean }>} slots
 */
export function firstAvailableSlot(slots) {
  return slots.find((slot) => slot.available !== false) ?? null
}

/**
 * @param {Array<{ starts_at: string, available?: boolean }>} slots
 */
export function countAvailableSlots(slots) {
  return slots.filter((slot) => slot.available !== false).length
}
